// Mock data for Insust landing page

export const solutions = [
  {
    id: 'spatial-foundations',
    detailPage: '/solutions/basemap-solutions.html',
    eyebrow: 'Solution',
    title: 'Spatial Foundations & Digital Basemaps',
    subtitle: 'Build on authoritative geospatial infrastructure',
    description: 'Enterprise-grade digital basemaps and spatial reference systems that provide the foundational layer for all geospatial intelligence initiatives. Ensure accuracy, consistency, and reliability across your organization.',
    capabilities: [
      'High-resolution multi-layer digital basemaps',
      'Authoritative spatial reference frameworks',
      'Real-time map tile serving infrastructure',
      'Custom cartographic styling and symbology',
      'Seamless integration with existing GIS systems'
    ],
    image: '/assets/basemap/Basemap_frontpage.png'
  },
  {
    id: 'data-acquisition',
    detailPage: '/solutions/surveying-services.html',
    eyebrow: 'Solution',
    title: 'Data Acquisition & Survey Intelligence',
    subtitle: 'Capture and process spatial data at scale',
    description: 'Advanced drone, satellite, and ground-based survey solutions powered by AI for rapid data capture, processing, and quality assurance. Transform raw spatial data into decision-ready intelligence.',
    capabilities: [
      'Automated drone survey planning and execution',
      'LiDAR and photogrammetry processing',
      'Multi-spectral and hyperspectral analysis',
      'Ground control point optimization',
      'Quality assurance and validation workflows'
    ],
    image: '/assets/survey/Survey_frontpage.mp4'
  },
  {
    id: 'geotagging-asset',
    detailPage: '/solutions/geotagging-monitoring.html',
    eyebrow: 'Solution',
    title: 'Geotagging, Asset Mapping & Monitoring',
    subtitle: 'Track and manage spatial assets in real-time',
    description: 'Comprehensive asset mapping and monitoring solutions that provide continuous visibility into your distributed infrastructure, equipment, and resources with precise geolocation intelligence.',
    capabilities: [
      'Automated asset discovery and tagging',
      'Real-time location tracking and monitoring',
      'Condition assessment and maintenance scheduling',
      'Mobile field data collection apps',
      'Integration with IoT sensors and telemetry'
    ],
    image: '/assets/geotag/Geotag_frontpage_20_9.mp4'
  },
  {
    id: 'change-detection',
    detailPage: '/solutions/change-detection.html',
    eyebrow: 'Solution',
    title: 'Change Detection & Impact Assessment',
    subtitle: 'Monitor spatial change and quantify impact',
    description: 'AI-powered temporal analysis that automatically detects, classifies, and quantifies spatial changes across landscapes, infrastructure, and urban environments for proactive decision-making.',
    capabilities: [
      'Multi-temporal satellite imagery analysis',
      'Automated change detection algorithms',
      'Land use and land cover change tracking',
      'Environmental impact quantification',
      'Disaster assessment and recovery monitoring'
    ],
    image: '/assets/change-detection/change_detection_frontpage.mp4'
  },
  {
    id: 'site-suitability',
    detailPage: '/solutions/site-suitability.html',
    eyebrow: 'Solution',
    title: 'Site Suitability & Location Intelligence',
    subtitle: 'Identify optimal locations with spatial science',
    description: 'Advanced multi-criteria spatial analysis that evaluates thousands of location factors to identify the best sites for expansion, development, logistics, or investment decisions.',
    capabilities: [
      'Multi-criteria decision analysis (MCDA)',
      'Demographic and market proximity analysis',
      'Infrastructure and accessibility scoring',
      'Environmental and regulatory constraints',
      'Risk-adjusted location recommendations'
    ],
    image: '/assets/site-suitability/Site_suitability_frontpage.png'
  },
  {
    id: 'advanced-modeling',
    detailPage: '/solutions/geospatial-modelling.html',
    eyebrow: 'Solution',
    title: 'Advanced Geospatial Modeling',
    subtitle: 'Simulate and predict spatial phenomena',
    description: 'Sophisticated spatial modeling and simulation capabilities that predict outcomes, optimize networks, and simulate scenarios to support complex strategic decisions.',
    capabilities: [
      'Predictive spatial analytics and forecasting',
      'Network optimization and routing',
      'Terrain and viewshed analysis',
      'Flood risk and environmental modeling',
      'Scenario planning and simulation tools'
    ],
    image: '/assets/advanced-modeling/Adv_spatial_modelling_frontpage.mp4'
  },
  {
    id: 'geoanalytics',
    detailPage: '/solutions/geoanalytics-bi.html',
    eyebrow: 'Solution',
    title: 'GeoAnalytics & BI',
    subtitle: 'Transform spatial data into business intelligence',
    description: 'Enterprise business intelligence platform with native spatial analytics capabilities. Visualize, analyze, and share location-based insights through interactive dashboards and reports.',
    capabilities: [
      'Interactive spatial dashboards and KPIs',
      'Advanced spatial query and analysis',
      'Automated reporting and alerting',
      'Integration with BI tools (Power BI, Tableau)',
      'Role-based access and data governance'
    ],
    image: '/assets/geoanalytics/Geoanalytics_BI_frontpage.png'
  },
  {
    id: 'training-support',
    detailPage: '/solutions/training-support.html',
    eyebrow: 'Solution',
    title: 'Training & Support',
    subtitle: 'Build spatial intelligence capabilities',
    description: 'Comprehensive training programs and ongoing support services that empower your teams to leverage geospatial intelligence effectively and maximize platform value.',
    capabilities: [
      'Role-based training programs and certification',
      'Hands-on workshops and use case development',
      'Dedicated technical support and consultation',
      'Knowledge base and learning resources',
      'Platform updates and continuous improvement'
    ],
    image: '/assets/training/Training_frontpage.png'
  }
];

export const products = [
  {
    id: 'property-intelligence',
    name: 'Property Intelligence Platform',
    status: 'Live',
    audience: 'Real Estate Investors, Developers, Financial Institutions',
    description: 'The next-generation AI operating system for real estate investment. Search, analyze, rank, and verify property opportunities through one conversational platform — turning fragmented research into clear, data-backed decisions.',
    features: [
      '10-step deterministic ranking pipeline (TOPSIS + PROMETHEE MCDM)',
      'SHAP-based explainable rankings — see exactly why each property ranked',
      'Live data verification against OpenStreetMap (hospitals, schools, metro)',
      'Hazard screening — auto-excludes flood zones, noise corridors',
      'Bayesian adaptive personalization that learns from user behavior',
    ],
    impact: '90% faster due diligence process',
    detailPage: true
  },
  {
    id: 'industrial-logistics',
    name: 'Industrial & Logistics Location Optimizer',
    status: 'Upcoming',
    audience: 'Logistics Companies, Warehousing, Manufacturing',
    description: 'Advanced site selection platform for industrial and logistics facilities, optimizing for transportation networks, labor markets, infrastructure, and operational costs.',
    features: [
      'Multi-modal transportation network analysis',
      'Labor market and workforce accessibility',
      'Utility infrastructure and capacity scoring',
      'Cost optimization and scenario planning',
      'Supplier and customer proximity analysis'
    ],
    impact: '30% reduction in logistics costs'
  },
  {
    id: 'geomarketing',
    name: 'GeoMarketing & Market Intelligence',
    status: 'Upcoming',
    audience: 'Retail, FMCG, Financial Services',
    description: 'Location-based market intelligence platform that identifies expansion opportunities, analyzes trade areas, and optimizes market penetration strategies.',
    features: [
      'Trade area analysis and customer profiling',
      'Competitive landscape mapping',
      'Market potential and penetration scoring',
      'Site cannibalization and network optimization',
      'Campaign targeting and performance tracking'
    ],
    impact: '45% higher expansion success rate'
  },
  {
    id: 'supply-chain',
    name: 'Supply Chain Optimization & Logistics Intelligence',
    status: 'Upcoming',
    audience: 'Supply Chain Managers, Distribution Networks',
    description: 'End-to-end supply chain intelligence platform with network design, route optimization, facility location planning, and real-time logistics visibility.',
    features: [
      'Network design and facility location planning',
      'Dynamic route optimization and scheduling',
      'Inventory positioning and flow analysis',
      'Risk exposure and resilience modeling',
      'Real-time tracking and performance analytics'
    ],
    impact: '25% reduction in transportation costs'
  }
];

export const industries = [
  {
    id: 'government-urban',
    name: 'Government, Urban Planning & Smart Cities',
    subtitle: 'Smarter, faster, more sustainable urban decisions',
    icon: 'Landmark',
    image: '/assets/industries/1a.png',
    description: 'We redefine how cities are planned and governed by transforming spatial data into real-time intelligence for smarter, faster, and more sustainable urban decision-making.'
  },
  {
    id: 'environment-climate',
    name: 'Environmental Management & Climate Sustainability',
    subtitle: 'Predictive intelligence for climate-resilient strategies',
    icon: 'Leaf',
    image: '/assets/industries/2a.png',
    description: 'We turn environmental data into predictive intelligence, enabling organizations to assess risks, ensure compliance, and drive resilient, sustainable climate strategies.'
  },
  {
    id: 'infrastructure-engineering',
    name: 'Infrastructure, Engineering & Construction',
    subtitle: 'Precision and real-time control for every project',
    icon: 'Building2',
    image: '/assets/industries/3c.png',
    description: 'We bring precision and real-time control to infrastructure projects through advanced geospatial technologies, enabling smarter design, efficient execution, and reduced risks.'
  },
  {
    id: 'agriculture-food',
    name: 'Agriculture & Food Security',
    subtitle: 'Data-driven agriculture and sustainable food production',
    icon: 'Sprout',
    image: '/assets/industries/4a.png',
    description: 'We empower data-driven agriculture by transforming geospatial insights into actionable intelligence for crop monitoring, land optimization, and sustainable food production.'
  },
  {
    id: 'renewable-energy',
    name: 'Renewable Energy & Natural Resource Development',
    subtitle: 'Precise site selection and optimized energy planning',
    icon: 'Zap',
    image: '/assets/industries/5a.png',
    description: 'We accelerate sustainable resource development through geospatial intelligence, enabling precise site selection, risk assessment, and optimized energy planning.'
  },
  {
    id: 'business-retail',
    name: 'Business, Retail & Market Intelligence',
    subtitle: 'Location intelligence for smarter expansion',
    icon: 'ShoppingBag',
    image: '/assets/industries/6b.png',
    description: 'We drive business growth with location intelligence, turning market and customer data into actionable insights for smarter expansion and optimized operations.'
  },
  {
    id: 'disaster-response',
    name: 'Disaster Risk Management & Emergency Response',
    subtitle: 'Faster, smarter response and resilient recovery',
    icon: 'ShieldAlert',
    image: '/assets/industries/7a.png',
    description: 'We enable faster, smarter disaster response by transforming real-time data into actionable insights for risk assessment, preparedness, and resilient recovery.'
  },
  {
    id: 'research-analytics',
    name: 'Research, Analytics & Capacity Building',
    subtitle: 'Innovation through geospatial analytics and AI',
    icon: 'GraduationCap',
    image: '/assets/industries/8a.png',
    description: 'We advance innovation through geospatial analytics and AI-driven modelling while empowering professionals with the skills to lead in spatial intelligence.'
  },
  {
    id: 'enterprise-gis',
    name: 'Enterprise GIS & Digital Transformation',
    subtitle: 'Scalable GIS adoption across core operations',
    icon: 'Network',
    image: '/assets/industries/9a.png',
    description: 'We drive enterprise digital transformation by embedding geospatial intelligence into core operations, enabling scalable GIS adoption and data-driven decision-making.'
  }
];

export const impactMetrics = [
  {
    value: '70%',
    label: 'Faster Due Diligence',
    description: 'Accelerated property assessment'
  },
  {
    value: '30%',
    label: 'Lower Logistics Cost',
    description: 'Optimized facility placement'
  },
  {
    value: '45%',
    label: 'Higher Expansion Success',
    description: 'Data-driven site selection'
  },
  {
    value: '25%',
    label: 'Transport Cost Reduction',
    description: 'Network optimization'
  }
];

export const platformPillars = [
  {
    icon: 'Brain',
    title: 'AI-powered geospatial analytics',
    description: 'Machine learning models that extract intelligence from spatial data automatically'
  },
  {
    icon: 'Target',
    title: 'Decision-grade spatial models',
    description: 'Validated analytical frameworks that support strategic decisions with confidence'
  },
  {
    icon: 'Layers',
    title: 'Industry-ready intelligence layers',
    description: 'Pre-built spatial data layers and analytics tailored for specific industries'
  },
  {
    icon: 'Lightbulb',
    title: 'Scenario-based planning tools',
    description: 'Interactive modeling capabilities to evaluate alternatives and optimize outcomes'
  }
];

export const courses = [
  {
    id: 'geoai-ml',
    number: '01',
    topic: 'GeoAI & Machine Learning',
    title: 'GeoAI & ML',
    description: 'Build ML models for feature extraction, classification, and predictive spatial analytics using Python and GIS platforms.',
    duration: '10 Weeks',
    format: 'Online · Instructor-Led',
    status: 'Enrolling',
    cohort: 'Coming Soon',
    cohortLabel: 'Cohort 04 · 2026',
    seats: { filled: 32, total: 45 },
    price: '₹7999',
    proPrice: null,
    image: '/assets/courses/Course_1.png',
    brochure: '/assets/courses/GeoAI_Premium_Brochure_Insust.pdf',
    stripeColor: '#6b3fa0'
  },
  {
    id: 'on-demand-project-training',
    number: '02',
    topic: 'Project Training',
    title: 'On-Demand Geospatial Project Training',
    description: 'Tailored, project-based training delivered on your timeline — customized curriculum aligned to your organization\u2019s real geospatial projects and datasets.',
    duration: 'Flexible',
    format: 'On-Demand · Custom',
    status: 'Open',
    cohort: 'Custom Schedule',
    cohortLabel: 'On Your Timeline',
    seats: { filled: 0, total: 0 },
    price: 'Custom',
    proPrice: null,
    image: '/assets/courses/Course_3_On_demand.png',
    brochure: '/assets/courses/GeoAI_Premium_Brochure_Insust.pdf',
    stripeColor: '#1a6b4a',
    hideCTA: true
  },
  {
    id: 'adv-spatial-data-science',
    number: '03',
    topic: 'Data Science & Analytics',
    title: 'Advanced Spatial Data Science & Analytics',
    description: 'Spatial statistics, big geospatial data workflows, and enterprise analytics pipelines for advanced spatial analysis.',
    duration: '12 Weeks',
    format: 'Online · Instructor-Led',
    status: 'Enrolling',
    cohort: 'TBD',
    cohortLabel: 'Cohort 02 · 2026',
    seats: { filled: 20, total: 45 },
    price: null,
    proPrice: null,
    image: '/assets/courses/Course_2_Adv_data_science_analytics.png',
    brochure: '/assets/courses/GeoAI_Premium_Brochure_Insust.pdf',
    stripeColor: '#c89b3c'
  },
  // {
  //   id: 'webgis-dev',
  //   number: '04',
  //   topic: 'Web-GIS & Development',
  //   title: 'Web-GIS & Spatial Application Development',
  //   description: 'Build interactive web-based GIS platforms and spatial dashboards for visualization and decision support.',
  //   duration: '8 Weeks',
  //   format: 'Online · Instructor-Led',
  //   status: 'Enrolling',
  //   cohort: 'Jun 02 — Jul 25',
  //   cohortLabel: 'Cohort 04 · 2026',
  //   seats: { filled: 14, total: 45 },
  //   price: '$179',
  //   proPrice: '$119',
  //   image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=700&q=80',
  //   stripeColor: '#c05e1a'
  // },
  // {
  //   id: 'applied-projects',
  //   number: '05',
  //   topic: 'Applied Projects',
  //   title: 'Applied Geospatial Project Training',
  //   description: 'Real-world projects — urban planning, environmental monitoring, disaster analysis, and infrastructure mapping.',
  //   duration: '6 Weeks',
  //   format: 'Online · Project-Based',
  //   status: 'Enrolling',
  //   cohort: 'Jun 02 — Jul 11',
  //   cohortLabel: 'Cohort 05 · 2026',
  //   seats: { filled: 19, total: 45 },
  //   price: '$149',
  //   proPrice: '$99',
  //   image: 'https://images.unsplash.com/photo-1519501025264-65ba15a82390?w=700&q=80',
  //   stripeColor: '#1a6b4a'
  // },
  // {
  //   id: 'advanced-analytics',
  //   number: '06',
  //   topic: 'Advanced Analytics',
  //   title: 'Advanced Spatial Data Science & Analytics',
  //   description: 'Spatial statistics, big geospatial data workflows, and enterprise analytics pipelines.',
  //   duration: '12 Weeks',
  //   format: 'Online · Instructor-Led',
  //   status: 'Coming Soon',
  //   cohort: 'Q3 2026',
  //   cohortLabel: 'Launching Soon',
  //   seats: { filled: 0, total: 45 },
  //   price: 'TBA',
  //   proPrice: null,
  //   image: 'https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=700&q=80',
  //   stripeColor: '#c89b3c'
  // }
];
